/**
 * The Strip Card rows of the pool's Settings tab (ISSUE-1294), mounted by
 * `features/poolScreen/poolTypePanes.tsx` above Chat and Notifications.
 * After v1's strip_settings_tab.dart: max spots per member (an admin edits
 * it, a member reads it), the first game (read only) and the pool rules,
 * whose max spots sentence comes from the same server value the board
 * enforces. Lowering the cap never takes a row away from anyone.
 */
import {
  useStripsPoolGet,
  useStripsSettingsUpdate,
} from "@sportsmet/contest-sdk/private";
import { Button, Card, InlineBanner, SavedNote, Skeleton } from "@sportsmet/ui";
import { QueryClientContext } from "@tanstack/react-query";
import { type ReactNode, useContext, useEffect, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import type { typePoolTypePaneProps } from "../poolScreen/poolTypePaneProps";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import {
  STRIP_FIRST_GAME_GONE,
  STRIP_RULES_HEADING,
  STRIP_SETTINGS_FIRST_GAME_LABEL,
  STRIP_SETTINGS_HEADING,
  STRIP_SETTINGS_LOAD_FAILED,
  STRIP_SETTINGS_SAVE_LABEL,
  STRIP_MAX_SPOTS_LABEL,
  STRIP_TRY_AGAIN_LABEL,
  stripMaxSpotsChoiceLabel,
  stripMaxSpotsSummary,
} from "./stripCardCopy";
import { StripMaxSpotsField } from "./StripMaxSpotsField";
import { stripRulesText } from "./stripRulesText";
import { stripKickoffLabel } from "./stripTime";

export const StripCardSettingsSection = (
  props: typePoolTypePaneProps,
): ReactNode => {
  // The pane registry's own test mounts every pane bare, with no providers.
  const hasQueryClient = useContext(QueryClientContext) !== undefined;
  return (
    <div
      className="flex flex-col gap-3"
      data-testid="strip-card-settings-section"
      data-pool-id={props.poolId}
    >
      {hasQueryClient ? <StripSettingsBody poolId={props.poolId} /> : null}
    </div>
  );
};

const StripSettingsBody = ({ poolId }: { poolId: string }): ReactNode => {
  const timeZone = resolveViewerTimeZone();
  const read = useStripsPoolGet({ poolId });
  const update = useStripsSettingsUpdate();
  const data = read.data?.data ?? null;
  const serverValue = data?.settings.maxSpotsPerMember ?? null;
  const [draft, setDraft] = useState<number | null>(serverValue);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setDraft(serverValue);
  }, [serverValue]);

  if (read.isPending) {
    return <Skeleton shape="card" />;
  }
  if (data === null) {
    return (
      <InlineBanner
        variant="warning"
        message={STRIP_SETTINGS_LOAD_FAILED}
        action={{
          label: STRIP_TRY_AGAIN_LABEL,
          onAction: () => {
            void read.refetch();
          },
        }}
      />
    );
  }

  const canManage = data.viewer.canManage;
  const firstGame =
    data.games.find((game) => game.eventId === data.settings.firstEventId) ??
    null;

  async function save(): Promise<void> {
    let result: Awaited<ReturnType<typeof update.mutateAsync>>;
    try {
      result = await update.mutateAsync({ poolId, maxSpotsPerMember: draft });
    } catch {
      // The app's MutationCache already raised the offline modal.
      return;
    }
    if (showApiError(result)) return;
    setIsSaved(true);
  }

  return (
    <>
      <Card data-testid="strip-settings-card">
        <div className="flex flex-col gap-4">
          <h2 className="text-body-strong text-ink">
            {STRIP_SETTINGS_HEADING}
          </h2>
          {canManage ? (
            <div className="flex flex-col gap-2">
              <StripMaxSpotsField
                value={draft}
                onChange={(next) => {
                  setDraft(next);
                  setIsSaved(false);
                }}
              />
              <p className="text-caption text-ink-secondary">
                {stripMaxSpotsSummary(draft)}
              </p>
              <div className="flex items-center gap-3">
                <Button
                  variant="secondary"
                  data-testid="strip-settings-save"
                  disabled={draft === serverValue}
                  loading={update.isPending}
                  onClick={() => {
                    void save();
                  }}
                >
                  {STRIP_SETTINGS_SAVE_LABEL}
                </Button>
                <SavedNote saved={isSaved} />
              </div>
            </div>
          ) : (
            <div
              className="flex flex-col gap-1"
              data-testid="strip-settings-max-spots"
            >
              <span className="text-caption text-ink-secondary">
                {STRIP_MAX_SPOTS_LABEL}
              </span>
              <span className="text-body text-ink">
                {stripMaxSpotsChoiceLabel(serverValue)}
              </span>
            </div>
          )}
          <div
            className="flex flex-col gap-1"
            data-testid="strip-settings-first-game"
          >
            <span className="text-caption text-ink-secondary">
              {STRIP_SETTINGS_FIRST_GAME_LABEL}
            </span>
            {firstGame === null ? (
              <span className="text-body text-ink">
                {STRIP_FIRST_GAME_GONE}
              </span>
            ) : (
              <>
                <span className="text-body text-ink">{firstGame.label}</span>
                <span className="text-caption text-ink-secondary">
                  {stripKickoffLabel({
                    kickoffAt: firstGame.kickoffAt,
                    timeZone,
                  })}
                </span>
              </>
            )}
          </div>
        </div>
      </Card>
      <Card data-testid="strip-rules-card">
        <div className="flex flex-col gap-3">
          <h2 className="text-body-strong text-ink">{STRIP_RULES_HEADING}</h2>
          <ul className="flex list-disc flex-col gap-2 pl-6">
            {stripRulesText({ maxSpotsPerMember: serverValue }).map((line) => (
              <li key={line} className="text-body text-ink">
                {line}
              </li>
            ))}
          </ul>
        </div>
      </Card>
    </>
  );
};
